// Copy for the pages that are hand-built JSX rather than data-driven.
//
// These pages used to hold their text inline, which meant scripts/prerender.cjs
// had nothing to render and they shipped an empty <div id="root"> to anything
// that doesn't run JavaScript. Moving the words here - and having the same
// components read them back - gives the prerenderer a source without creating a
// second copy of the text that could drift from what a visitor sees. A crawler
// being served different words from a visitor is cloaking, so "one source" is
// the whole point of this file, not tidiness.
//
// Keep it free of JSX and browser globals: scripts/seo/load-esm.cjs loads it in
// Node at build time. Icons are fine - that loader stubs node_modules imports,
// and src/data/pages.js already carries them the same way.
//
// Presentation stays in the components: kickers, headings and leads live here as
// plain strings, and the component decides how they are animated or styled.

import {
  PhoneIncoming, BrainCircuit, LineChart,
  Building2, TrendingUp, Headphones, Handshake,
  Mic, PhoneOutgoing, FileText, Gauge, Radio, BookOpen, UserCheck, Webhook,
  Boxes, Network, ListTree, Users, Hash, Clock, PhoneCall, MonitorSmartphone, ShieldCheck,
  Target, Cpu, Globe,
  Rocket, Users as Users2, Heart,
  CreditCard, Home, CalendarCheck, Headset, TrendingUp as TrendingUp2,
} from "lucide-react";

export const LANDING = {
  hero: {
    // Split the way the <h1> animates: two masked lines, with the last word
    // carrying the blue gradient. Joined back together for the prerendered
    // heading, so there is only one wording to keep correct.
    h1Lines: ["A phone system", "that talks "],
    h1Accent: "back.",
    lead:
      "Real phone numbers, extensions and routing — plus an AI agent that answers, speaks and sells for you. In English, Hindi and Hinglish. On infrastructure you control.",
  },

  howItWorks: {
    kicker: "How it works",
    h2: "From a ringing phone to written intelligence — in one call.",
    steps: [
      {
        n: "01",
        icon: PhoneIncoming,
        title: "The caller dials",
        body: "Assign any real business number to an AI agent — it picks up instantly, on your own caller ID.",
      },
      {
        n: "02",
        icon: BrainCircuit,
        title: "The AI answers, live",
        body: "A natural two-way conversation with barge-in. It speaks your customer's language and knows your business.",
      },
      {
        n: "03",
        icon: LineChart,
        title: "Insights land in your dashboard",
        body: "Full transcript, sentiment and intent — pushed to your CRM the moment the call ends.",
      },
    ],
  },

  features: {
    ai: {
      kicker: "The hero feature",
      h2: "An AI voice agent built into the network — not bolted on as a widget.",
      lead:
        "It connects at the same audio layer as a real phone call. Lower latency, full call control, genuinely conversational.",
      // The one card given its own tile and blue accent, ahead of the grid.
      highlight: {
        t: "Fluent in English, Hindi & Hinglish",
        d: "Natural mixed-language conversation with correct grammar matched to the voice — a real differentiator for Indian markets.",
      },
      items: [
        { icon: Mic, t: "Real two-way conversations", d: "Natural barge-in so it never talks over the caller." },
        { icon: PhoneOutgoing, t: "Inbound & outbound", d: "Answers and places calls on your real number." },
        { icon: FileText, t: "Recorded & transcribed", d: "Turn-by-turn logs saved for every AI call." },
        { icon: Gauge, t: "Auto sentiment & intent", d: "Every call scored — no manual review." },
        { icon: Radio, t: "Live monitoring", d: "Supervisors listen in as the call happens." },
        { icon: BookOpen, t: "Per-agent knowledge base", d: "Upload docs; it answers from them." },
        { icon: UserCheck, t: "Smart human hand-off", d: "Transfers a live call when it matters." },
        { icon: Webhook, t: "Webhooks / CRM", d: "Transcript + outcome pushed instantly." },
      ],
    },
    pbx: {
      kicker: "The solid foundation underneath",
      h3: "A complete, carrier-grade phone system",
      aside: "BYOC · Reseller-ready",
      items: [
        { icon: Boxes, t: "Multi-tenant platform" },
        { icon: Network, t: "Extensions & trunks" },
        { icon: ListTree, t: "IVR menus & call flows" },
        { icon: Users, t: "Ring groups & queues" },
        { icon: Hash, t: "DID number management" },
        { icon: Clock, t: "Business hours rules" },
        { icon: PhoneCall, t: "Call recording" },
        { icon: MonitorSmartphone, t: "Browser softphone" },
        { icon: ShieldCheck, t: "IP whitelisting & RBAC" },
      ],
    },
  },

  manifesto: {
    kicker: "Why OwnPBX",
    h2: "Built different, on purpose.",
    chapters: [
      {
        n: "01",
        title: "Not a SaaS wrapper.",
        body: "OwnPBX runs on real, self-owned telephony infrastructure — full control over call handling, recording and routing that API-only 'AI calling' startups simply can't offer.",
      },
      {
        n: "02",
        title: "AI at the media layer.",
        body: "The agent taps directly into the live audio stream of a call. That means lower latency and a far more natural conversation than typical bolt-on voicebots.",
      },
      {
        n: "03",
        title: "Your data stays yours.",
        body: "Self-hosted with full data ownership. No dependence on a third-party calling platform — your call data lives on infrastructure you control.",
      },
      {
        n: "04",
        title: "One platform, both worlds.",
        body: "Traditional PBX for teams who want real extensions and desk phones, and AI-first automation for teams who want calls handled without hiring. Mix both.",
      },
    ],
  },

  whoFor: {
    kicker: "Who it's for",
    h2: "Made for teams that live on the phone.",
    personas: [
      { icon: Building2, t: "SMBs", d: "A modern phone system without hiring a full reception or call-center team." },
      { icon: TrendingUp, t: "Sales teams", d: "Automated outbound follow-ups that still sound personal — on your own number." },
      { icon: Headphones, t: "Support teams", d: "Every call logged, transcribed and analyzed automatically." },
      { icon: Handshake, t: "Agencies & resellers", d: "White-label a full phone system with AI built in for your own clients." },
    ],
  },
};

// A <h1> rendered as several masked lines, flattened to one string. The line
// break is standing in for a space, so put it back; the accent word has no
// break before it and is appended as-is.
export const headline = (lines, accent = "") =>
  `${lines.join(" ")}${accent}`.replace(/\s+/g, " ").trim();

export const landingHeadline = () =>
  headline(LANDING.hero.h1Lines, LANDING.hero.h1Accent);

export const ABOUT = {
  kicker: "About",
  h1Lines: ["We're building the", "phone system that", "talks back."],
  lead:
    "OwnPBX started with a simple conviction: businesses shouldn't have to choose between a real phone system and modern AI. So we built both into one platform — carrier-grade telephony with an AI voice agent living inside the network, speaking your customers' language.",
  mission: {
    h2: "Our mission",
    paras: [
      "Most \"AI calling\" products are thin wrappers around someone else's telephony API. That means limited control, higher latency and your data sitting on a platform you don't own.",
      "We took the harder path — running on real, self-owned infrastructure and connecting the AI agent at the same audio layer as a genuine phone call. The result is lower latency, natural conversation, and full ownership of your call data.",
      // Not "most of our customers want a bit of both": there are no paying
      // customers yet, so that is a claim we cannot make. Built for, not
      // trusted by.
      "One platform serves teams who want traditional extensions and desk phones, and teams who want calls handled entirely by AI. It is built for the teams that want a bit of both.",
    ],
  },
  beliefs: {
    h2: "What we believe",
    values: [
      { icon: Cpu, t: "AI at the media layer", d: "We build where the audio actually flows — not as a bolt-on widget." },
      { icon: ShieldCheck, t: "Own your data", d: "Self-hosted, carrier-grade infrastructure you control." },
      { icon: Globe, t: "Built for our markets", d: "Truly multilingual — English, Hindi and Hinglish from day one." },
      { icon: Target, t: "Ship what's real", d: "We market what's live and working, not slideware." },
    ],
  },
  stats: [
    { k: "2-way", v: "Real-time AI conversations" },
    { k: "3", v: "Languages spoken fluently" },
    { k: "100%", v: "Calls recorded & analyzed" },
    { k: "1", v: "Platform: PBX + AI" },
  ],
  cta: "Want to see it in action?",
};

export const CAREERS = {
  kicker: "Careers",
  h1Lines: ["Build the future", "of business calls."],
  lead:
    "We're a small, senior team building carrier-grade telephony with AI at its core. If you like hard problems, real ownership and shipping things people use every day — come talk to us.",
  perks: [
    { icon: Rocket, t: "Real ownership", d: "Small team, big surface area — your work ships fast." },
    { icon: Users2, t: "Remote-first", d: "Work from anywhere, with hubs in India." },
    { icon: Heart, t: "Health & learning", d: "Insurance, learning budget and real time off." },
  ],
  roles: [
    { title: "Senior Voice Infrastructure Engineer", team: "Engineering", type: "Full-time", loc: "Remote / Bengaluru" },
    { title: "AI/ML Engineer — Speech & LLM", team: "AI", type: "Full-time", loc: "Remote" },
    { title: "Full-Stack Engineer (React / FastAPI)", team: "Engineering", type: "Full-time", loc: "Remote / Bengaluru" },
    { title: "Product Designer", team: "Design", type: "Full-time", loc: "Remote" },
    { title: "Partnerships & Reseller Manager", team: "Go-to-market", type: "Full-time", loc: "Bengaluru" },
    { title: "Customer Success Lead", team: "Success", type: "Full-time", loc: "Remote" },
  ],
};

export const PRICING = {
  kicker: "Pricing",
  h1Lines: ["Simple pricing.", "Serious phone system."],
  lead:
    "Priced per extension, per month — choose your region and whether you need telephony included. Enterprise is custom-quoted.",
  // The tier tables themselves come from the API at runtime, so they are not
  // here and are not prerendered: a price written into the HTML would be a
  // price we might not be charging.
  faq: {
    h2: "Frequently asked",
    items: [
      { q: "What's the difference between Platform only and Platform + Telephony?", a: "Platform only gives you the full PBX admin — extensions, IVR, call reports, recordings — and you bring your own carrier (BYOC) for the actual calling. Platform + Telephony includes calling itself, billed per extension with no per-minute charges." },
      { q: "Is the AI Voice Agent included?", a: "The AI Voice Agent is priced separately from the PBX platform — a flat ₹1,799/month plan plus ₹4.50 per AI-agent minute used. Switch to the \"AI Voice Agent\" tab above for the full details." },
      { q: "How does per-extension pricing work?", a: "Each tier has a minimum and maximum extension count. You pay the tier's per-extension rate multiplied by how many extensions you need, within that range — once you outgrow a tier's maximum, move up to the next one." },
      { q: "What does bring-your-own-carrier (BYOC) mean?", a: "You can connect your own telephony carrier rather than being locked into ours — giving you control over call costs and routing." },
      { q: "Can I white-label OwnPBX for my clients?", a: "Yes. The Enterprise tier includes white-labeling, and our Partner programs are built exactly for resellers and agencies." },
      { q: "Do you support Hindi and Hinglish?", a: "Yes — the AI agent is fluent in English, Hindi and natural Hinglish, with grammar matched to the chosen voice." },
    ],
  },
  cta: "Not sure which plan fits?",
};

export const DEMO = {
  kicker: "Get started",
  h1Lines: ["Book your", "OwnPBX demo."],
  lead:
    "Tell us how your calls run today and we will set the demo up around it — the console, your call routing, and an AI agent on a live call.",
};

export const AGENT_DEMOS = {
  kicker: "AI Voice Agent · Live demos",
  h1Lines: ["Talk to an AI agent", "built for your industry."],
  lead:
    "Same OwnPBX AI, trained on a different persona for each use case below. Pick one and chat with it live — it's the real thing, not a script. (Every company and scenario here is fictional, made up for the demo.)",
  // The whole persona, widget fields included, so there is one array rather
  // than a display copy and a prerender copy that can fall out of step. The
  // prerenderer takes only t and d; agentId, icon, greeting and fiction drive
  // the live card.
  personas: [
    {
      agentId: "agent_demo_collections",
      label: "Riya — Collections Assistant",
      icon: CreditCard,
      t: "Collections & follow-ups",
      d: "Chases overdue payments professionally — verifies details, offers a payment plan, never sounds aggressive.",
      greeting: "this is Riya calling from Meridian Finance about your account. Is now a good time to talk?",
      fiction: "Meridian Finance is a made-up company.",
    },
    {
      agentId: "agent_demo_realestate",
      label: "Arjun — Property Assistant",
      icon: Home,
      t: "Real estate",
      d: "Answers listing questions, discusses price and amenities, and books site visits.",
      greeting: "I'm Arjun from Skyline Realty. Which property can I help you with today?",
      fiction: "Skyline Realty is a made-up company.",
    },
    {
      agentId: "agent_demo_appointment",
      label: "Meera — Appointment Assistant",
      icon: CalendarCheck,
      t: "Appointment booking",
      d: "Checks availability and books, reschedules or cancels appointments — no hold music, no back-and-forth calls.",
      greeting: "I'm Meera from Bright Smile Dental. Would you like to book, reschedule or cancel an appointment?",
      fiction: "Bright Smile Dental is a made-up clinic.",
    },
    {
      agentId: "agent_demo_support",
      label: "Kabir — Support Assistant",
      icon: Headset,
      t: "Customer service",
      d: "Handles order status, returns and basic troubleshooting for a fictional electronics brand.",
      greeting: "I'm Kabir from Nova Electronics support. What can I help you with today?",
      fiction: "Nova Electronics is a made-up brand.",
    },
    {
      agentId: "agent_demo_sales",
      label: "Simran — Sales Assistant",
      icon: TrendingUp2,
      t: "Sales",
      d: "Qualifies leads, explains pricing tiers, and offers to book a demo call — for a fictional B2B software company.",
      greeting: "I'm Simran from Cloudstack Software. What does your team need help with?",
      fiction: "Cloudstack Software is a made-up company.",
    },
  ],
};

export const NUMBERS = {
  kicker: "Numbers & coverage",
  h1Lines: ["VoIP numbers,", "in 150+ countries."],
  lead:
    "Local, mobile-series, toll-free and UIFN numbers, delivered over SIP — no SIM cards, no physical lines. They ring straight into your OwnPBX extensions, IVR, queues or an AI voice agent. Start in India or the US, and add any country below as you grow.",
  coverageH2: "Where you can get a number",
};

export const ANALYTICS = {
  kicker: "PBX Platform · Reporting",
  h1Lines: ["Know exactly what", "your phone lines", "are doing."],
  lead:
    "A few of the reports every tenant gets out of the box — from live call analytics to agent performance and full call detail records.",
};

// A four-step form. There is no prose to speak of, so the prerendered body is
// just the heading - enough that the page is not served empty, without
// inventing copy that no visitor would see.
export const SIGNUP = {
  kicker: "Get started",
  h1: "Create your account",
};

// The 404 page. Read by src/pages/NotFoundPage.jsx and by
// scripts/seo/render-html.cjs, so the words a visitor sees and the words in
// build/404.html are the same words — two copies would drift, and serving a
// crawler text no visitor sees is cloaking.
export const NOT_FOUND = {
  kicker: "404",
  h1: "That page isn't here.",
  lead:
    "The address may have a typo in it, or the page may have moved since the link was written. Nothing is broken — here is where most people are going.",
  links: [
    { t: "Pricing", d: "What the platform and the AI agent cost", href: "/pricing" },
    { t: "AI voice agent", d: "What it does on a call", href: "/ai/overview" },
    { t: "Cloud PBX", d: "Extensions, routing and numbers", href: "/pbx/features" },
    { t: "Guides", d: "How business calling actually works", href: "/guides" },
    { t: "Documentation", d: "Setup and configuration", href: "/docs" },
    { t: "Contact", d: "Talk to a person", href: "/contact" },
  ],
};
